/**
 * /audio-converter and /audio-joiner (SEO_BRIEF_11 section 2.1). The page
 * (tools/audio_convert.html) loads this bundle beside boot.js, the shared shell. It owns the
 * controls, the progress line, the result and the Download button, and starts the worker
 * (`audio-convert-worker.ts`, where mediabunny lives) on the first job, so a visitor who never
 * adds a file never downloads it.
 *
 * The job is the root's `data-audio-convert`:
 *   convert  one file in, MP3 (128, 192 or 320 kbps) or WAV out. Choosing another format after a
 *            result converts the same file again. A format page (SEO_BRIEF_12 section 3:
 *            /m4a-to-mp3 and the other three) is this job with its preset in the root's
 *            `data-audio-target` (`mp3-192` or `wav`): an MP3 page offers the three rates, a
 *            WAV page no choice, and that is what it saves.
 *   join     two or more files in the order set by the drag handles or the up and down buttons,
 *            out as one MP3, with the gap or crossfade chosen.
 *
 * Every word is in the template (`data-audio-label`, the banners, the row template); this file
 * carries only format names.
 *
 * THE USAGE COUNTER (base/src/lib/usage.ts), under the root's `data-usage-tool` slug: "loaded" once
 * per file whose sound came out (each file of a join), "failed" once per file that could not be
 * used, "downloaded" for each Download, in the saved type. "no result" is the counter's own, when
 * the page is left with a file loaded and nothing downloaded.
 */

import { mountDropzone } from '../../base/src/lib/dropzone.ts';
import { clock, fileSize } from '../../base/src/lib/format.ts';
import { type UsageCounter, usageCounter } from '../../base/src/lib/usage.ts';
import {
  DEFAULT_KBPS,
  moveItem,
  parseSpacing,
  parseTarget,
} from '../../../../src/media/audio-choices.ts';
import { mp3WasmUrl } from '../../../../src/media/mp3-assets.ts';
import { dropIndex, routeConvertDrop, targetName, targetType } from './lib/convert-page.ts';
import type {
  ConvertTarget,
  FromWorker,
  JoinFailure,
  ConvertFailure,
  ToWorker,
} from './lib/convert-protocol.ts';
import { codecName, droppedFormat, savedFormat } from './lib/labels.ts';

type ErrorKey = Exclude<ConvertFailure | JoinFailure, 'cancelled' | 'too-few'>;

function buildId(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content.trim() || null;
}

/** Same build-id rule as the other workers: never yesterday's bundle. */
function workerUrl(): string {
  const build = buildId();
  return build
    ? `/static/build/audio/audio-convert-worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/audio/audio-convert-worker.js';
}

function q<T extends Element = HTMLElement>(root: ParentNode, sel: string): T | null {
  return root.querySelector<T>(sel);
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking in the same task cancels the download in some engines.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** What both jobs share: the worker, the banners, the progress line, the result and Download. */
interface Shell {
  readonly usage: UsageCounter | null;
  label(key: string): string;
  slot(name: string, text: string): void;
  showError(key: ErrorKey | null): void;
  setStatus(text: string): void;
  setWorking(on: boolean): void;
  clearResult(): void;
  showResult(blob: Blob, name: string, container: string, type: string): void;
  /** Sends a job; the answer comes to `onAnswer` unless another job or a cancel replaced it. */
  send(make: (id: number) => ToWorker, onAnswer: (reply: FromWorker) => void): void;
  /** Stops the job in flight, if any. */
  stop(): void;
  busy(): boolean;
}

function shell(root: HTMLElement): Shell | null {
  const working = q(root, '[data-audio-working]');
  const bar = q(root, '[data-audio-bar]');
  const fill = q(root, '[data-audio-fill]');
  const pctSlot = q(root, '[data-audio-working] [data-slot="pct"]');
  const cancel = q<HTMLButtonElement>(root, '[data-audio-cancel]');
  const result = q(root, '[data-audio-result]');
  const save = q<HTMLButtonElement>(root, '[data-audio-save]');
  const status = q(root, '[data-audio-status]');
  if (!save) return null;

  const usageTool = root.dataset['usageTool'] ?? '';
  const usage = usageTool ? usageCounter(usageTool) : null;

  let worker: Worker | null = null;
  let nextId = 1;
  let current: { id: number; onAnswer: (reply: FromWorker) => void } | null = null;
  let done: { blob: Blob; name: string; container: string } | null = null;

  const label = (key: string): string =>
    q(root, `[data-audio-label="${key}"]`)?.textContent?.trim() ?? '';
  const slot = (name: string, text: string) => {
    for (const el of root.querySelectorAll<HTMLElement>(`[data-slot="${name}"]`)) {
      if (!el.closest('template')) el.textContent = text;
    }
  };
  const setStatus = (text: string) => {
    if (status) status.textContent = text;
  };
  const setProgress = (fraction: number | null) => {
    if (bar) bar.dataset['indeterminate'] = fraction === null ? 'true' : 'false';
    const pct = fraction === null ? 0 : Math.round(fraction * 100);
    fill?.style.setProperty('--progress-pct', `${pct}%`);
    if (pctSlot) pctSlot.textContent = String(pct);
  };
  const setWorking = (on: boolean) => {
    if (working) working.hidden = !on;
    if (on) setProgress(null);
  };
  const showError = (key: ErrorKey | null) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-audio-error]')) {
      el.hidden = el.dataset['audioError'] !== key;
    }
  };
  const clearResult = () => {
    done = null;
    save.disabled = true;
    if (result) result.hidden = true;
  };
  const showResult = (blob: Blob, name: string, container: string, type: string) => {
    done = { blob, name, container };
    slot('type', `${type} · ${name} · ${fileSize(blob.size)}`);
    slot('format', container.toUpperCase());
    if (result) result.hidden = false;
    save.disabled = false;
    setStatus(label('ready'));
  };

  const onMessage = (event: MessageEvent<FromWorker>) => {
    const reply = event.data;
    if (current === null || reply.id !== current.id) return;
    if (reply.kind === 'progress') {
      setProgress(reply.fraction);
      return;
    }
    const { onAnswer } = current;
    current = null;
    setWorking(false);
    onAnswer(reply);
  };

  const start = (): Worker => {
    if (worker) return worker;
    const w = new Worker(workerUrl(), { type: 'module' });
    w.onmessage = onMessage;
    w.onerror = (event) => {
      // A worker that failed to load (offline, blocked) gets the "went wrong" banner rather than
      // leaving the page on a progress bar.
      event.preventDefault();
      w.terminate();
      worker = null;
      if (current) {
        current = null;
        setWorking(false);
        showError('encoder');
      }
    };
    worker = w;
    return w;
  };

  const stop = () => {
    if (current === null) return;
    worker?.postMessage({ kind: 'cancel', id: current.id } satisfies ToWorker);
    current = null;
    setWorking(false);
  };

  const send = (make: (id: number) => ToWorker, onAnswer: (reply: FromWorker) => void) => {
    stop();
    clearResult();
    showError(null);
    setStatus('');
    const id = nextId++;
    current = { id, onAnswer };
    setWorking(true);
    start().postMessage(make(id));
  };

  cancel?.addEventListener('click', () => {
    stop();
    setStatus(label('cancelled'));
  });

  save.addEventListener('click', () => {
    if (!done) return;
    saveBlob(done.blob, done.name);
    usage?.downloaded(savedFormat(done.container));
    setStatus(label('saved'));
  });

  return {
    usage,
    label,
    slot,
    showError,
    setStatus,
    setWorking,
    clearResult,
    showResult,
    send,
    stop,
    busy: () => current !== null,
  };
}

/* ── /audio-converter ─────────────────────────────────────────────────── */

function mountConvert(root: HTMLElement, page: Shell): void {
  const host = q(root, '[data-dropzone-host]');
  if (!host) return;
  const radios = [...root.querySelectorAll<HTMLInputElement>('[data-audio-format]')];
  // A format page's preset (SEO_BRIEF_12 section 3): what it saves when no radio is checked, and
  // all a WAV page saves, since it offers no choice.
  const preset = parseTarget(root.dataset['audioTarget'] ?? '');
  const chosen = (): ConvertTarget =>
    parseTarget(radios.find((r) => r.checked)?.value ?? '') ??
    preset ?? { kind: 'mp3', kbps: DEFAULT_KBPS };
  let file: File | null = null;

  const run = (f: File) => {
    const target = chosen();
    page.slot('format', targetType(target));
    page.send(
      (id) => ({
        kind: 'convert',
        id,
        file: f,
        inputName: f.name,
        target,
        wasmUrl: target.kind === 'mp3' ? mp3WasmUrl(buildId()) : null,
      }),
      (reply) => {
        if (reply.kind !== 'converted') return;
        const answer = reply.result;
        if (answer.ok) {
          page.usage?.loaded(f, droppedFormat(f));
          const length = answer.durationS ? ` · ${clock(answer.durationS)}` : '';
          // The codec as /mp4-to-mp3 names it: AAC, Opus, Vorbis, FLAC, PCM…
          page.slot('sound', `${codecName(answer.sourceCodec)}${length}`);
          page.showResult(answer.blob, answer.name, answer.container, targetName(target));
          return;
        }
        if (answer.reason === 'cancelled') {
          page.setStatus(page.label('cancelled'));
          return;
        }
        console.warn('[audio-convert]', answer.reason, answer.detail);
        page.usage?.failed(f, droppedFormat(f));
        page.showError(answer.reason);
      },
    );
  };

  mountDropzone(host, {
    onFiles: (dropped) => {
      // One file at a time: the first one dropped is the one converted.
      const f = dropped[0];
      if (!f) return;
      if (routeConvertDrop(f) === 'start') {
        file = f;
        run(f);
        return;
      }
      page.stop();
      page.clearResult();
      page.setStatus('');
      file = null;
      page.usage?.failed(f, droppedFormat(f));
      page.showError('unreadable');
    },
  });

  // Another format for the file already here: convert it again, as chosen.
  for (const radio of radios) {
    radio.addEventListener('change', () => {
      page.slot('format', targetType(chosen()));
      if (file) run(file);
    });
  }
}

/* ── /audio-joiner ────────────────────────────────────────────────────── */

interface Item {
  readonly id: number;
  readonly file: File;
}

function mountJoin(root: HTMLElement, page: Shell): void {
  const host = q(root, '[data-dropzone-host]');
  const list = q<HTMLOListElement>(root, '[data-join-list]');
  const rowTemplate = q<HTMLTemplateElement>(root, '[data-join-row]');
  const few = q(root, '[data-join-few]');
  const which = q(root, '[data-join-which]');
  const startButton = q<HTMLButtonElement>(root, '[data-join-start]');
  const radios = [...root.querySelectorAll<HTMLInputElement>('[data-join-spacing]')];
  if (!host || !list || !rowTemplate || !startButton) return;

  let items: Item[] = [];
  let nextItem = 1;

  const showWhich = (name: string | null) => {
    if (!which) return;
    which.hidden = name === null;
    const s = which.querySelector('[data-slot="file"]');
    if (s) s.textContent = name ?? '';
  };

  /** Anything that changes the order or the spacing makes the last result stale. */
  const changed = () => {
    page.stop();
    page.clearResult();
    page.setStatus('');
    render();
  };

  const render = () => {
    list.replaceChildren();
    for (const [index, item] of items.entries()) {
      const fragment = rowTemplate.content.cloneNode(true) as DocumentFragment;
      const row = fragment.querySelector<HTMLElement>('[data-join-item]');
      if (!row) continue;
      row.dataset['joinId'] = String(item.id);
      const name = row.querySelector('[data-slot="name"]');
      if (name) name.textContent = item.file.name;
      const size = row.querySelector('[data-slot="length"]');
      if (size) size.textContent = fileSize(item.file.size);
      const up = row.querySelector<HTMLButtonElement>('[data-join-up]');
      const down = row.querySelector<HTMLButtonElement>('[data-join-down]');
      if (up) up.disabled = index === 0;
      if (down) down.disabled = index === items.length - 1;
      list.append(fragment);
    }
    list.hidden = items.length === 0;
    if (few) few.hidden = items.length === 0 || items.length >= 2;
    startButton.disabled = items.length < 2;
  };

  const indexOfRow = (row: Element | null): number => {
    const id = Number((row as HTMLElement | null)?.dataset['joinId']);
    return items.findIndex((i) => i.id === id);
  };

  // Up, down and remove, by delegation: the rows are redrawn on every change.
  list.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('button');
    if (!button || button.disabled) return;
    const at = indexOfRow(button.closest('[data-join-item]'));
    if (at < 0) return;
    let focusAt = at;
    let focusSel = '';
    if (button.hasAttribute('data-join-up')) {
      items = moveItem(items, at, at - 1);
      focusAt = at - 1;
      focusSel = focusAt === 0 ? '[data-join-down]' : '[data-join-up]';
    } else if (button.hasAttribute('data-join-down')) {
      items = moveItem(items, at, at + 1);
      focusAt = at + 1;
      focusSel = focusAt === items.length - 1 ? '[data-join-up]' : '[data-join-down]';
    } else if (button.hasAttribute('data-join-remove')) {
      items = items.filter((_, i) => i !== at);
      focusAt = Math.min(at, items.length - 1);
      focusSel = '[data-join-remove]';
    } else {
      return;
    }
    showWhich(null);
    changed();
    // Keyboard and screen-reader users keep their place.
    const rows = list.querySelectorAll('[data-join-item]');
    rows[focusAt]?.querySelector<HTMLElement>(focusSel)?.focus();
  });

  // The drag handle: Pointer Events, so a finger on Android or iPhone and a mouse work alike. The
  // row itself moves in the list while the pointer is down; the order is read back on release.
  // The pointer is followed on the document, not on the handle: moving the row in the list takes
  // it out of the document for a moment, which ends any pointer capture on the handle, and a
  // finger that strays off the 44px handle must still move the row and drop it.
  list.addEventListener('pointerdown', (event) => {
    const handle = (event.target as Element).closest<HTMLElement>('[data-join-handle]');
    const row = handle?.closest<HTMLElement>('[data-join-item]');
    if (!handle || !row || page.busy() || event.button !== 0) return;
    event.preventDefault();
    const pointer = event.pointerId;
    row.classList.add('is-dragging');
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pointer) return;
      const others = [...list.querySelectorAll<HTMLElement>('[data-join-item]')].filter(
        (r) => r !== row,
      );
      const middles = others.map((r) => {
        const box = r.getBoundingClientRect();
        return box.top + box.height / 2;
      });
      const to = dropIndex(middles, e.clientY);
      const before = others[to] ?? null;
      if (row.nextElementSibling !== before || (before === null && list.lastElementChild !== row)) {
        list.insertBefore(row, before);
      }
    };
    const end = (e: PointerEvent) => {
      if (e.pointerId !== pointer) return;
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
      row.classList.remove('is-dragging');
      const order = [...list.querySelectorAll<HTMLElement>('[data-join-item]')].map((r) =>
        Number(r.dataset['joinId']),
      );
      const next = order
        .map((id) => items.find((i) => i.id === id))
        .filter((i): i is Item => i !== undefined);
      const moved = next.some((item, i) => item !== items[i]);
      items = next;
      if (moved) changed();
      else render();
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
  });

  mountDropzone(host, {
    onFiles: (dropped) => {
      let refused: File | null = null;
      for (const f of dropped) {
        if (routeConvertDrop(f) === 'start') {
          items.push({ id: nextItem++, file: f });
        } else {
          page.usage?.failed(f, droppedFormat(f));
          refused = f;
        }
      }
      changed();
      page.showError(refused ? 'unreadable' : null);
      showWhich(refused ? refused.name : null);
    },
  });

  for (const radio of radios) radio.addEventListener('change', changed);

  startButton.addEventListener('click', () => {
    if (items.length < 2) return;
    const spacing = parseSpacing(radios.find((r) => r.checked)?.value ?? '') ?? {
      kind: 'gap',
      seconds: 0,
    };
    const joined = [...items];
    showWhich(null);
    page.send(
      (id) => ({
        kind: 'join',
        id,
        files: joined.map((i) => ({ blob: i.file, name: i.file.name })),
        spacing,
        wasmUrl: mp3WasmUrl(buildId()),
      }),
      (reply) => {
        if (reply.kind !== 'joined') return;
        const answer = reply.result;
        if (answer.ok) {
          for (const item of joined) page.usage?.loaded(item.file, droppedFormat(item.file));
          page.showResult(
            answer.blob,
            answer.name,
            answer.container,
            `MP3 ${page.label('rate')} · ${clock(answer.durationS)}`,
          );
          return;
        }
        if (answer.reason === 'cancelled') {
          page.setStatus(page.label('cancelled'));
          return;
        }
        if (answer.reason === 'too-few') {
          render();
          return;
        }
        console.warn('[audio-join]', answer.reason, answer.detail);
        const bad = answer.index === null ? null : joined[answer.index];
        if (bad) {
          page.usage?.failed(bad.file, droppedFormat(bad.file));
          showWhich(bad.file.name);
        }
        page.showError(answer.reason);
      },
    );
  });

  render();
}

const root = document.querySelector<HTMLElement>('[data-audio-convert]');
const page = root ? shell(root) : null;
if (root && page) {
  if (root.dataset['audioConvert'] === 'join') mountJoin(root, page);
  else mountConvert(root, page);
}
